<template>
  <div class="space-y-4">
    <template v-if="showTemplates">
      <div class="flex justify-between">
        <h2>{{ t('templates') }}</h2>
        <CVButton variant="danger" @click="showTemplates = !showTemplates">
          <div class="flex items-center gap-2">
            {{ t('hideTemplates') }}
            <XIcon class="size-4" />
          </div>
        </CVButton>
      </div>
      <VueDraggableNext
        :list="formValues.templates"
        class="flex flex-col gap-2"
        handle=".handle"
        @change="changeTemplatesOrder"
      >
        <TemplateListItem
          v-for="(template, index) in formValues.templates"
          :key="template.id"
          :template="template"
          :has-error="itemsWithErrors.templates.includes(index)"
          @click="showTemplateModal = index"
          @delete="deleteTemplate(index)"
        />
      </VueDraggableNext>
      <AddItemButton :label="t('addTemplate')" @click="addTemplate" />
      <Divider />
    </template>

    <div class="flex justify-between">
      <h2>{{ t('modifiers') }}</h2>
      <div class="flex gap-4">
        <CVButton @click="showCalculatorModal = true">
          {{ t('calculateExamplePrice') }}
          <CalculatorIcon class="size-4" />
        </CVButton>
        <CVButton v-if="!showTemplates" @click="showTemplates = true">
          {{ t('editTemplates') }}
          <PencilIcon class="size-4" />
        </CVButton>
      </div>
    </div>
    <template v-for="template in formValues.templates" :key="template.id">
      <h3 class="text-lg font-medium">{{ template.name }}</h3>
      <VueDraggableNext
        :list="getModifiersOfTemplateId(template.id)"
        class="flex flex-col gap-2"
        handle=".handle"
        @change="changeModifiersOrder"
      >
        <ModifierListItem
          v-for="{ modifier, index } in getModifiersOfTemplateId(template.id)"
          :key="index"
          :modifier="modifier"
          :has-error="itemsWithErrors.modifiers.includes(index)"
          @click="showModifierModal = { index, template }"
          @delete="deleteModifier(index)"
        />
      </VueDraggableNext>
      <AddItemButton :label="t('addModifier')" @click="addModifier(template)" />
    </template>

    <Divider />

    <div class="flex justify-end">
      <CVButton
        variant="default"
        :is-loading="isCreating"
        :disabled="!formHasChanged(unchangedValues, formValues)"
        @click="onSubmit"
        >{{ t('save') }}</CVButton
      >
    </div>

    <Modal :model-value="showTemplateModal !== null" @update:model-value="showTemplateModal = null">
      <TemplateItemForm :template-index="showTemplateModal!" @close="showTemplateModal = null" />
    </Modal>

    <Modal
      :model-value="showModifierModal !== null"
      content-class="!overflow-y-visible"
      @update:model-value="showModifierModal = null"
    >
      <ModifierItemForm
        :modifier-index="showModifierModal!.index"
        :template="showModifierModal!.template"
        @close="showModifierModal = null"
      />
    </Modal>

    <Modal :model-value="showCalculatorModal" @update:model-value="showCalculatorModal = false">
      <ExamplePriceCalculator
        :pricing-modifiers="formValues"
        @close="showCalculatorModal = false"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import type {
  CreatePricingModifiersDto,
  PricingModifierDto,
  PricingModifierTemplateDto,
} from '@carvia/ros-client-types';
import { useFieldArray, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form.ts';
import { formHasChanged } from '@/hooks/use-form-has-changed.ts';
import Divider from '@/components/Divider.vue';
import TemplateListItem from './TemplateListItem.vue';
import TemplateItemForm from './TemplateItemForm.vue';
import ModifierItemForm from './ModifierItemForm.vue';
import Modal from '@/components/Modal.vue';
import { CalculatorIcon, PencilIcon, XIcon } from 'lucide-vue-next';
import { Alert } from '@/services/alert.service';
import ModifierListItem from './ModifierListItem.vue';
import { pricingModifiersSchema } from '@/validation/pricing-modifiers.schema.ts';
import { VueDraggableNext } from 'vue-draggable-next';
import AddItemButton from '@/views/yield-management/ui/AddItemButton.vue';
import ExamplePriceCalculator from './ExamplePriceCalculator.vue';

const props = defineProps<{
  initialPricingModifiersDto: CreatePricingModifiersDto;
  isCreating: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: CreatePricingModifiersDto): void;
}>();

const { t } = useI18n();

const showTemplates = ref(false);
const showTemplateModal = ref<number | null>(null);
const showModifierModal = ref<{ index: number; template: PricingModifierTemplateDto } | null>(null);
const showCalculatorModal = ref(false);

const {
  handleSubmit,
  values: formValues,
  errors,
} = useForm({
  initialValues: props.initialPricingModifiersDto,
  keepValuesOnUnmount: true,
  validationSchema: pricingModifiersSchema,
});

const itemsWithErrors = computed(() => {
  const templateErrors = new Set<number>();
  const modifierErrors = new Set<number>();
  Object.keys(errors.value).forEach((key) => {
    const index = parseInt(key.match(/\[(\d+)\]/)?.[1] ?? '0');
    if (key.startsWith('templates')) {
      templateErrors.add(index);
    } else if (key.startsWith('modifiers')) {
      modifierErrors.add(index);
    }
  });
  return {
    templates: Array.from(templateErrors),
    modifiers: Array.from(modifierErrors),
  };
});

const templateFields = useFieldArray('templates');
const modifierFields = useFieldArray('modifiers');

const addTemplate = () => {
  const newId = Math.max(...formValues.templates.map((template) => template.id)) + 1;
  templateFields.push({ id: newId, name: '', conditions: {} });
  showTemplateModal.value = formValues.templates.length - 1;
};

const addModifier = (template: PricingModifierTemplateDto) => {
  modifierFields.push({ templateId: template.id, modifier: 1, conditions: {} });
  showModifierModal.value = { index: formValues.modifiers.length - 1, template };
};

const deleteTemplate = async (index: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteTemplate'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (isDenied) {
    modifierFields.replace(
      formValues.modifiers.filter((m) => m.templateId !== formValues.templates[index].id),
    );
    templateFields.remove(index);
  }
};

const deleteModifier = async (index: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteModifier'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (isDenied) {
    modifierFields.remove(index);
  }
};

const changeTemplatesOrder = ({ moved }: { moved: { newIndex: number; oldIndex: number } }) => {
  const { newIndex, oldIndex } = moved;
  templateFields.move(oldIndex, newIndex);
};

const changeModifiersOrder = ({
  moved,
}: {
  moved: { element: { index: number; modifier: PricingModifierDto }; newIndex: number };
}) => {
  const { element, newIndex } = moved;
  let nthOccurrence = 0;
  let actualNewIndex = 0;
  for (let i = 0; i < formValues.modifiers.length; i++) {
    if (formValues.modifiers[i].templateId === element.modifier.templateId) {
      if (nthOccurrence === newIndex) {
        actualNewIndex = i;
        break;
      }
      nthOccurrence++;
    }
  }
  modifierFields.move(element.index, actualNewIndex);
};

const getModifiersOfTemplateId = (id: number) => {
  return formValues.modifiers
    .map((m, index) => ({ modifier: m, index }))
    .filter((m) => m.modifier.templateId === id);
};

const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit(async (values) => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallyCreateNewVersion'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('createNewVersion'),
    denyButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }
  unchangedValues.value = cloneDeep(formValues);
  emit('onSubmit', values);
});

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "templates": "Templates",
    "modifiers": "Modifiers",
    "name": "Name",
    "conditions": "Conditions",
    "addTemplate": "Add Template",
    "addModifier": "Add Modifier",
    "reallyDeleteTemplate": "All modifiers that use this template will be deleted too. Do you really want to delete this template?",
    "reallyDeleteModifier": "Do you really want to delete this modifier?",
    "reallyCreateNewVersion": "Do you really want to create a new pricing modifiers version? This will change the outcome of all future price calculations.",
    "createNewVersion": "Create new version",
    "editTemplates": "Edit templates",
    "hideTemplates": "Hide templates",
    "calculateExamplePrice": "Calculate example price"
  },
  "de": {
    "templates": "Vorlagen",
    "modifiers": "Modifikatoren",
    "name": "Name",
    "conditions": "Bedingungen",
    "addTemplate": "Template hinzufügen",
    "addModifier": "Modifikator hinzufügen",
    "reallyDeleteTemplate": "Alle Modifikatoren, die dieses Template nutzen, werden auch gelöscht. Möchtest du dieses Template wirklich löschen?",
    "reallyDeleteModifier": "Möchtest du diesen Modifikator wirklich löschen",
    "reallyCreateNewVersion": "Möchtest du wirklich eine neue Version der Preismodifikatoren erstellen? Dies wird das Ergebnis aller zukünftigen Preisberechnungen ändern.",
    "createNewVersion": "Neue Version erstellen",
    "editTemplates": "Vorlagen bearbeiten",
    "hideTemplates": "Vorlagen ausblenden",
    "calculateExamplePrice": "Beispielpreis berechnen"
  },
  "es": {
    "templates": "Plantillas",
    "modifiers": "Modificadores",
    "name": "Nombre",
    "conditions": "Condiciones",
    "addTemplate": "Añadir plantilla",
    "addModifier": "Añadir modificador",
    "reallyDeleteTemplate": "También se eliminarán todos los modificadores que usen esta plantilla. ¿Seguro que quieres eliminarla?",
    "reallyDeleteModifier": "¿Seguro que quieres eliminar este modificador?",
    "reallyCreateNewVersion": "¿Seguro que quieres crear una nueva versión de modificadores de precio? Cambiará el resultado de todos los cálculos futuros.",
    "createNewVersion": "Crear nueva versión",
    "editTemplates": "Editar plantillas",
    "hideTemplates": "Ocultar plantillas",
    "calculateExamplePrice": "Calcular precio de ejemplo"
  }
}
</i18n>
